/**
 * Product Card Component
 * Handles hover interactions, size selection, and AJAX add to cart
 */

class ProductCard {
  constructor(element) {
    this.card = element;
    this.productId = this.card.dataset.productId;
    this.sizeButtons = this.card.querySelectorAll('.product-card__size-btn');
    this.addToCartBtn = this.card.querySelector('.product-card__add-to-cart');
    this.selectedVariantId = null;

    this.init();
  }

  init() {
    // Set initial variant: from size selector, or default variant for products with no variants
    if (this.sizeButtons.length > 0) {
      const firstBtn = this.sizeButtons[0];
      this.selectedVariantId = firstBtn.dataset.variantId;
      this.updateVariantPrice(firstBtn);
    } else {
      this.selectedVariantId = this.card.dataset.defaultVariantId || null;
    }

    // Bind events
    this.bindEvents();
  }

  bindEvents() {
    // Size button clicks
    this.sizeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleSizeSelect(e));
    });

    // Add to cart button click
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  handleSizeSelect(e) {
    const btn = e.currentTarget;

    // Remove active class from all buttons
    this.sizeButtons.forEach(b => b.classList.remove('active'));

    // Add active class to clicked button
    btn.classList.add('active');

    // Update selected variant and price (default-content price spans)
    this.selectedVariantId = btn.dataset.variantId;
    this.updateVariantPrice(btn);
  }

  updateVariantPrice(btn) {
    const price = btn.dataset.variantPrice || '';
    const compare = btn.dataset.variantCompare || '';
    const saleEl = this.card.querySelector('.product-card__default-content .product-card__price--sale');
    const compareWrap = this.card.querySelector('.product-card__default-content .product-card__price--compare');
    const regularEl = this.card.querySelector('.product-card__default-content .product-card__price--regular');
    if (!saleEl && !regularEl) return;

    if (compare) {
      if (saleEl) { saleEl.textContent = price; saleEl.style.display = ''; }
      if (compareWrap) { compareWrap.innerHTML = '<s>' + compare + '</s>'; compareWrap.style.display = ''; }
      if (regularEl) regularEl.style.display = 'none';
    } else {
      if (saleEl) saleEl.style.display = 'none';
      if (compareWrap) compareWrap.style.display = 'none';
      if (regularEl) { regularEl.textContent = price; regularEl.style.display = ''; }
    }
  }

  getMessageEl() {
    return this.card.querySelector('.product-card__message');
  }

  getAddToCartErrorFallback() {
    return this.card.dataset.addToCartMaxMessage || 'Maximum quantity reached in your cart.';
  }

  getShortErrorMessage(data) {
    const apiMessage = (data.description || data.message || '').trim();
    if (!apiMessage) return this.getAddToCartErrorFallback();
    const lower = apiMessage.toLowerCase();
    if (lower.includes('sold out') || lower.includes('no longer in stock')) {
      return this.card.dataset.addToCartSoldOutMessage || 'Sold out.';
    }
    if (lower.includes('maximum quantity') || lower.includes('already in your cart')) {
      return this.getAddToCartErrorFallback();
    }
    if (apiMessage.length <= 35) return apiMessage;
    return this.card.dataset.addToCartErrorMessage || 'Couldn\'t add to cart.';
  }

  showMessage(text) {
    const el = this.getMessageEl();
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    el.classList.add('is-visible');
    if (this._messageTimer) clearTimeout(this._messageTimer);
    this._messageTimer = setTimeout(() => {
      el.hidden = true;
      el.classList.remove('is-visible');
      el.textContent = '';
    }, 4000);
  }

  async handleAddToCart() {
    if (!this.selectedVariantId) {
      console.error('No variant selected');
      return;
    }

    // Disable button and show loading state
    this.addToCartBtn.disabled = true;
    this.addToCartBtn.classList.add('loading');
    const originalText = this.addToCartBtn.textContent;
    this.addToCartBtn.textContent = 'ADDING';

    try {
      const formData = {
        items: [{
          id: this.selectedVariantId,
          quantity: 1
        }]
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        this.showMessage(this.getShortErrorMessage(data));
        this.addToCartBtn.textContent = originalText;
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('loading');
        return;
      }

      // Success feedback
      this.addToCartBtn.textContent = 'ADDED!';

      // Fetch updated cart state to get complete cart object
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      // Dispatch custom event for cart update with complete cart object
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: {
          cart: cart,
          addedItem: data.items ? data.items[0] : data
        }
      }));

      // Reset button after 2 seconds
      setTimeout(() => {
        this.addToCartBtn.textContent = originalText;
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('loading');
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showMessage(this.getAddToCartErrorFallback());
      this.addToCartBtn.textContent = originalText;
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.classList.remove('loading');
    }
  }
}

// Initialize function
function initProductCards(container = document) {
  const productCards = container.querySelectorAll('.product-card:not([data-initialized])');
  productCards.forEach(card => {
    new ProductCard(card);
    card.dataset.initialized = 'true';
  });
}

// Initialize all product cards on page load
document.addEventListener('DOMContentLoaded', () => {
  initProductCards();
});

// Re-initialize on Shopify section load (for Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  initProductCards(event.target);
});

// Watch for dynamic content changes (for AJAX-loaded content like product recommendations)
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself is a product card
            if (node.classList && node.classList.contains('product-card')) {
              if (!node.dataset.initialized) {
                new ProductCard(node);
                node.dataset.initialized = 'true';
              }
            }
            // Check for product cards within the node
            initProductCards(node);
          }
        });
      }
    });
  });

  // Start observing the document body for added nodes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// Export for manual initialization if needed
window.initProductCards = initProductCards;
